import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { PROJECT_MAX_DURATION, MEDIA_ITEM_MIN_DURATION } from 'consts'

import { pixelsToSeconds } from 'utils/timeline'
import { editorSelectors } from 'store/features/editor/selectors'
import { ITimelineItem } from 'types/timeline'
import useScrollWhenDragging from 'hooks/useScrollWhenDragging'
import { useTimelineContext } from 'context/TimelineContext'
import { sortArraysByKey } from 'store/features/projectData/utils'

const getDistanceToPrevItem = (
  items: ITimelineItem[],
  currentItemIndex: number
): number => {
  if (currentItemIndex === 0) {
    return 0
  }
  const currentItem = items[currentItemIndex]
  const prevItem = items[currentItemIndex - 1]
  const prevItemEnd = prevItem.startTime + prevItem.duration
  return currentItem.startTime - prevItemEnd
}

const getDistanceToNextItem = (
  items: ITimelineItem[],
  currentItemIndex: number
): number => {
  if (currentItemIndex === items.length - 1) {
    return 0
  }
  const currentItem = items[currentItemIndex]
  const nextItem = items[currentItemIndex + 1]
  const nextItemStart = nextItem.startTime
  return nextItemStart - (currentItem.startTime + currentItem.duration)
}

const useTimelineItemResize = (
  timelineItems: ITimelineItem[],
  isResizing: boolean
) => {
  const { scrollableContainerRef } = useTimelineContext()
  const [isScrollDisabled, setIsScrollDisabled] = useState(!isResizing)

  useEffect(() => {
    setIsScrollDisabled(!isResizing)
  }, [isResizing])

  useScrollWhenDragging({
    scrollableContainerRef,
    isDisabled: isScrollDisabled
  })

  const timelineItemsSorted = useMemo(
    () => sortArraysByKey('startTime', timelineItems),
    [timelineItems]
  )

  const [items, setItems] = useState(timelineItemsSorted)
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )

  useEffect(() => {
    setItems(timelineItemsSorted)
  }, [timelineItemsSorted])

  const handleLeftResize = (id: string, newWidth: number) => {
    const newDuration = pixelsToSeconds(newWidth, timelineRulerSliceDuration)
    if (newDuration <= MEDIA_ITEM_MIN_DURATION) {
      setIsScrollDisabled(true)
      return
    }

    const idx = timelineItemsSorted.findIndex(item => item.id === id)
    if (idx === -1) return

    const item = timelineItemsSorted[idx]
    const lastItem = timelineItemsSorted.at(-1)!
    const distanceToPrev = getDistanceToPrevItem(timelineItemsSorted, idx)
    const distanceToNext = getDistanceToNextItem(timelineItemsSorted, idx)
    const playbackSpeed = item.playbackSpeed ?? 1

    // Total empty timeline to the left of the current item: the immediate gap
    // plus any room before the chain (prior items slide as a block to expose it).
    const spaceToLeft = timelineItemsSorted[0].startTime + distanceToPrev
    const spaceToRight =
      PROJECT_MAX_DURATION -
      (lastItem.startTime + lastItem.duration) +
      distanceToNext
    // Left source available, expressed in timeline seconds — 1 timeline second
    // consumes `playbackSpeed` source seconds.
    const leftSource =
      item.trimStart !== undefined ? item.trimStart / playbackSpeed : Infinity
    const requested = newDuration - item.duration

    // The left handle only consumes left-side source. First, move the left
    // edge left as far as timeline space and source allow. If more is needed,
    // grow the right edge instead — still consuming left source — until the
    // source head is exhausted.
    const leftEdgeShift = Math.min(requested, spaceToLeft, leftSource)
    const rightEdgeShift = Math.min(
      requested - leftEdgeShift,
      leftSource - leftEdgeShift,
      spaceToRight
    )
    const actualDelta = leftEdgeShift + rightEdgeShift

    if (requested > 0 && actualDelta <= 0) {
      setIsScrollDisabled(true)
      return
    }

    setIsScrollDisabled(false)

    const precedingShift = -Math.max(leftEdgeShift - distanceToPrev, 0)
    const followingShift = Math.max(rightEdgeShift - distanceToNext, 0)
    setItems(() =>
      timelineItemsSorted.map((item, i) => {
        if (i < idx) {
          return precedingShift === 0
            ? item
            : { ...item, startTime: item.startTime + precedingShift }
        }
        if (i === idx) {
          return {
            ...item,
            startTime: item.startTime - leftEdgeShift,
            duration: item.duration + actualDelta,
            ...(item.trimStart !== undefined && {
              trimStart: item.trimStart - actualDelta * playbackSpeed
            })
          }
        }
        return followingShift === 0
          ? item
          : { ...item, startTime: item.startTime + followingShift }
      })
    )
  }

  const handleRightResize = (id: string, newWidth: number) => {
    const newDuration = pixelsToSeconds(newWidth, timelineRulerSliceDuration)
    const currentItemIndex = timelineItemsSorted.findIndex(
      item => item.id === id
    )
    if (currentItemIndex === -1) return

    const lastItem = timelineItemsSorted.at(-1)!
    const {
      duration: initialDuration,
      trimStart: initialTrimStart,
      originalDuration,
      playbackSpeed = 1
    } = timelineItemsSorted[currentItemIndex]
    const distanceToNext = getDistanceToNextItem(
      timelineItemsSorted,
      currentItemIndex
    )

    const availableSpaceFromRight =
      PROJECT_MAX_DURATION -
      (lastItem.startTime + lastItem.duration) +
      distanceToNext

    const durationDelta = newDuration - initialDuration
    const wouldExceedOriginalDuration =
      initialTrimStart !== undefined &&
      originalDuration !== undefined &&
      initialTrimStart + newDuration * playbackSpeed > originalDuration
    const wouldExceedAvailableSpace = availableSpaceFromRight < durationDelta

    if (
      wouldExceedOriginalDuration ||
      wouldExceedAvailableSpace ||
      newDuration <= MEDIA_ITEM_MIN_DURATION
    ) {
      setIsScrollDisabled(true)
      return
    }

    setIsScrollDisabled(false)

    setItems(() =>
      timelineItemsSorted.map((item, idx) => {
        if (idx === currentItemIndex) {
          return { ...item, duration: item.duration + durationDelta }
        }
        const shift =
          idx > currentItemIndex
            ? Math.max(durationDelta - distanceToNext, 0)
            : 0
        return shift === 0
          ? item
          : { ...item, startTime: item.startTime + shift }
      })
    )
  }

  return { items, handleLeftResize, handleRightResize }
}

export default useTimelineItemResize
